/* A source fabric ID selects fabric only; existing studios own pricing and purchasing. */
(() => {
  'use strict';
  if (window.KaliaSwatchDeepLink) return;
  const script = document.currentScript;
  const catalogUrl = script?.dataset.catalogUrl;
  let catalogRequest;
  const catalog = () => {
    if (!catalogRequest) catalogRequest = fetch(catalogUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(response => { if (!response.ok) throw new Error('Swatch reference unavailable.'); return response.json(); });
    return catalogRequest;
  };

  class KaliaSwatchDeepLink {
    static sourceId(url) {
      const values = new URL(url).searchParams.getAll('swatch');
      return values.length === 1 && /^[1-9]\d{0,29}$/.test(values[0]) ? values[0] : null;
    }
    static shadeSku(studio, sourceId) {
      const matches = studio.config?.skus?.filter(sku => String(sku.id) === sourceId) || [];
      const sku = matches.length === 1 ? matches[0] : null;
      if (!sku || !studio.nativeNames?.length || !studio.nativeNames.every(name => typeof sku.options?.[name] === 'string' && studio.nativeWidgets?.get(name)?.inputs.some(item => item.value === sku.options[name]))) return null;
      // Use the studio's own mapping/availability rules, never a source ID as a native variant ID.
      return typeof studio.nativeVariant === 'function' && studio.nativeVariant(sku) ? sku : null;
    }
    static draperyColor(studio, sourceId, data) {
      if (data?.version !== 1 || !Array.isArray(data.swatches)) return -1;
      const matches = data.swatches.filter(item => item.family === 'drapery' && item.productHandle === studio.dataset.handle && String(item.sourceSkuId) === sourceId);
      if (matches.length !== 1 || typeof matches[0].sourceOptions?.Color !== 'string') return -1;
      const label = matches[0].sourceOptions.Color;
      const colors = studio.config?.colors || [];
      const indices = colors.map((color, index) => color.label === label ? index : -1).filter(index => index >= 0);
      return indices.length === 1 ? indices[0] : -1;
    }
    constructor(studio, sourceId, loadCatalog = catalog) {
      this.studio = studio;
      this.sourceId = sourceId;
      this.isShade = studio.matches('kalia-product-studio');
      this.onInteraction = event => {
        if (!event.isTrusted) return;
        this.notice?.remove();
        this.stop();
      };
      studio.addEventListener('input', this.onInteraction, true);
      studio.addEventListener('change', this.onInteraction, true);
      this.observer = new MutationObserver(() => this.apply());
      this.observer.observe(studio, { childList: true, subtree: true });
      this.onReady = () => this.apply();
      studio.addEventListener('kalia:product-studio-ready', this.onReady);
      if (!sourceId) { this.finish(false); return; }
      if (!this.isShade) loadCatalog().then(data => {
        if (this.done) return;
        this.catalog = data;
        this.apply();
      }).catch(() => { if (!this.done) this.finish(false); });
      this.apply();
    }
    stop() {
      this.done = true;
      this.observer.disconnect();
      this.studio.removeEventListener('kalia:product-studio-ready', this.onReady);
      this.studio.removeEventListener('input', this.onInteraction, true);
      this.studio.removeEventListener('change', this.onInteraction, true);
    }
    finish(selected, label = '') {
      this.stop();
      const introduction = this.studio.querySelector('.kps-intro, .kd-intro');
      if (!introduction) return;
      const notice = document.createElement('p');
      notice.className = 'kalia-swatch-link-status';
      notice.dataset.swatchLinkState = selected ? 'selected' : 'unavailable';
      notice.setAttribute('role', 'status');
      notice.style.cssText = 'margin:12px 0 0;font-size:13px;line-height:1.5;color:#5c5e62';
      notice.textContent = selected ? `Swatch selected: ${label}. Customize your size and options below.` : 'We could not select this swatch. Please choose a fabric below.';
      introduction.insertAdjacentElement('afterend', notice);
      this.notice = notice;
      // The confirmation describes the arrival state only; remove it after a customer's edit.
      this.clearNotice = event => {
        if (!event.isTrusted) return;
        notice.remove();
        this.studio.removeEventListener('input', this.clearNotice, true);
        this.studio.removeEventListener('change', this.clearNotice, true);
      };
      this.studio.addEventListener('input', this.clearNotice, true);
      this.studio.addEventListener('change', this.clearNotice, true);
    }
    apply() {
      if (this.done) return;
      if (this.isShade) {
        if (!this.studio.ready) return;
        const sku = this.constructor.shadeSku(this.studio, this.sourceId);
        if (!sku) { this.finish(false); return; }
        // Commit the complete source option tuple together, avoiding intermediate color/style fallbacks.
        this.studio.nativeChoices = { ...sku.options };
        this.studio.update(true);
        this.finish(true, this.studio.nativeNames.map(name => sku.options[name]).join(' · '));
      } else {
        if (!this.catalog || !this.studio.config?.colors || !this.studio.querySelector('input[name="color"]')) return;
        const index = this.constructor.draperyColor(this.studio, this.sourceId, this.catalog);
        const input = index >= 0 ? this.studio.querySelector(`input[name="color"][value="${index}"]`) : null;
        if (!input || input.disabled) { this.finish(false); return; }
        input.checked = true;
        // The normal form change resets review confirmation and requotes any chosen hardware.
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.finish(true, this.studio.config.colors[index].label);
      }
    }
  }

  window.KaliaSwatchDeepLink = KaliaSwatchDeepLink;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('swatch')) return;
  const sourceId = KaliaSwatchDeepLink.sourceId(url.href);
  const attached = new WeakSet();
  const attach = studio => {
    if (!studio || attached.has(studio) || !studio.matches?.('kalia-product-studio, kalia-drapery')) return;
    attached.add(studio);
    new KaliaSwatchDeepLink(studio, sourceId);
  };
  document.addEventListener('kalia:product-studio-ready', event => attach(event.target));
  const initialize = () => document.querySelectorAll('kalia-product-studio, kalia-drapery').forEach(attach);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
